import { useCallback, useState, useEffect } from 'react';

import { actionsApi } from '../api/actionsApi';
import { useRuleStore } from '../state/ruleStore';
import {
  ActionDefinition,
  FormAction,
  Team,
  ActionSearchFilters,
  UseActionsReturn,
} from '../types/actions';
import {
  validateActions,
  validateActionDynamicFields,
  generateActionText,
  generateDynamicFields,
  filterActions,
  convertFromApiActions,
  createActionsInApi,
  updateActionsInApi,
  deleteActionsInApi,
} from '../utils/actionUtils';

export const useActions = (): UseActionsReturn => {
  const { setActions: setDraftActions } = useRuleStore();

  // Estado das ações
  const [actions, setActions] = useState<FormAction[]>([]);
  const [actionDefinitions, setActionDefinitions] = useState<ActionDefinition[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchFilters, setSearchFilters] = useState<ActionSearchFilters>({
    searchTerm: '',
  });

  // ✅ Sincronizar ações locais com Zustand
  useEffect(() => {
    console.log('🔄 Sincronizando ações com Zustand:', actions);
    setDraftActions(actions);
  }, [actions, setDraftActions]);

  // Carregar definições de ações
  const loadActionDefinitions = useCallback(async () => {
    if (actionDefinitions.length > 0) {
      console.log('⏭️ Action definitions já carregadas, pulando...');
      return;
    }

    console.log('🔄 Carregando action definitions...');
    setLoading(true);
    setError(null);

    try {
      const definitions = await actionsApi.getActionDefinitions();
      setActionDefinitions(definitions);
      console.log('✅ Action definitions carregadas:', definitions);
    } catch (err: any) {
      console.error('❌ Erro ao carregar action definitions:', err);
      setError(err.message || 'Erro ao carregar definições de ações');
    } finally {
      setLoading(false);
    }
  }, [actionDefinitions.length]);

  // Carregar equipes
  const loadTeams = useCallback(async () => {
    if (teams.length > 0) {
      console.log('⏭️ Equipes já carregadas, pulando...');
      return;
    }

    console.log('🔄 Carregando equipes...');
    setLoading(true);
    setError(null);

    try {
      const teamsData = await actionsApi.getTeams();
      setTeams(teamsData);
      console.log('✅ Equipes carregadas:', teamsData);
    } catch (err: any) {
      console.error('❌ Erro ao carregar equipes:', err);
      setError(err.message || 'Erro ao carregar equipes');
    } finally {
      setLoading(false);
    }
  }, [teams.length]);

  // Adicionar ação
  const addAction = useCallback(() => {
    console.log('➕ Adicionando nova ação');
    const newAction: FormAction = {
      actionKey: '',
      actionLabel: '',
      dynamicFields: {},
    };
    setActions(prev => [...prev, newAction]);
  }, []);

  // Remover ação
  const removeAction = useCallback((index: number) => {
    console.log(`🗑️ Removendo ação ${index}`);
    setActions(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Atualizar ação
  const updateAction = useCallback(
    (index: number, field: keyof FormAction, value: any) => {
      console.log(`🔄 Atualizando ação ${index}, campo: ${field}, valor:`, value);

      setActions(prev => {
        const updatedActions = [...prev];
        const action = { ...updatedActions[index] };

        if (field === 'actionKey') {
          action.actionKey = value;

          // Encontrar a definição da ação e atualizar o label
          const actionDef = actionDefinitions.find(def => def.id.toString() === value);
          if (actionDef) {
            action.actionLabel = actionDef.name;

            // Gerar campos dinâmicos baseados no config_schema
            const dynamicFields = generateDynamicFields(actionDef);
            const newDynamicFields: Record<string, any> = {};

            dynamicFields.forEach(field => {
              newDynamicFields[field.key] = field.type === 'boolean' ? field.default || false : '';
            });

            action.dynamicFields = newDynamicFields;
          } else {
            action.actionLabel = '';
            action.dynamicFields = {};
          }
        } else {
          (action as any)[field] = value;
        }

        updatedActions[index] = action;
        console.log(`✅ Ação ${index} atualizada:`, action);
        return updatedActions;
      });
    },
    [actionDefinitions]
  );

  // Atualizar campo dinâmico de uma ação
  const updateActionDynamicField = useCallback((index: number, fieldKey: string, value: any) => {
    console.log(`🔄 Atualizando campo dinâmico ${fieldKey} da ação ${index}:`, value);

    setActions(prev => {
      const updatedActions = [...prev];
      const action = { ...updatedActions[index] };

      action.dynamicFields = {
        ...action.dynamicFields,
        [fieldKey]: value,
      };

      updatedActions[index] = action;
      console.log(`✅ Campo dinâmico atualizado:`, action.dynamicFields);
      return updatedActions;
    });
  }, []);

  // Definir termo de busca
  const setSearchTerm = useCallback((term: string) => {
    setSearchFilters(prev => ({ ...prev, searchTerm: term }));
  }, []);

  // Filtrar ações
  const getFilteredActions = useCallback(() => {
    return filterActions(actionDefinitions, searchFilters);
  }, [actionDefinitions, searchFilters]);

  // Validar ações
  const validateActionsFn = useCallback(() => {
    return validateActions(actions);
  }, [actions]);

  // Validar campos dinâmicos de uma ação
  const validateActionDynamicFieldsFn = useCallback(
    (actionIndex: number) => {
      const action = actions[actionIndex];
      if (!action) return { isValid: true, errors: [] };

      const actionDef = actionDefinitions.find(def => def.id.toString() === action.actionKey);
      if (!actionDef) return { isValid: true, errors: [] };

      return validateActionDynamicFields(action, actionDef);
    },
    [actions, actionDefinitions]
  );

  // Gerar texto das ações
  const generateActionTextFn = useCallback(() => {
    return generateActionText(actions);
  }, [actions]);

  // Gerar campos dinâmicos
  const generateDynamicFieldsFn = useCallback((actionDefinition: ActionDefinition) => {
    return generateDynamicFields(actionDefinition);
  }, []);

  // Criar ações na API
  const createActionsInApiFn = useCallback(
    async (ruleId: string) => {
      console.log('🚀 Criando ações na API...');
      setLoading(true);
      setError(null);

      try {
        const success = await createActionsInApi(
          actions,
          ruleId,
          actionsApi.createAction,
          actionDefinitions
        );

        if (success) {
          console.log('✅ Ações criadas com sucesso na API');
          return true;
        } else {
          console.error('❌ Falha ao criar ações na API');
          setError('Falha ao criar ações na API');
          return false;
        }
      } catch (err: any) {
        console.error('❌ Erro ao criar ações na API:', err);
        setError(err.message || 'Erro ao criar ações na API');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [actions, actionDefinitions]
  );

  // Atualizar ações na API
  const updateActionsInApiFn = useCallback(
    async (ruleId: string) => {
      console.log('🔄 Atualizando ações na API...');
      setLoading(true);
      setError(null);

      try {
        const success = await updateActionsInApi(
          actions,
          ruleId,
          actionsApi.updateAction,
          actionDefinitions
        );

        if (success) {
          console.log('✅ Ações atualizadas com sucesso na API');
          return true;
        } else {
          console.error('❌ Falha ao atualizar ações na API');
          setError('Falha ao atualizar ações na API');
          return false;
        }
      } catch (err: any) {
        console.error('❌ Erro ao atualizar ações na API:', err);
        setError(err.message || 'Erro ao atualizar ações na API');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [actions, actionDefinitions]
  );

  // Deletar ações na API
  const deleteActionsInApiFn = useCallback(
    async (ruleId: string) => {
      console.log('🗑️ Deletando ações na API...');
      setLoading(true);
      setError(null);

      try {
        const success = await deleteActionsInApi(actions, ruleId, actionsApi.deleteAction);

        if (success) {
          console.log('✅ Ações deletadas com sucesso na API');
          return true;
        } else {
          console.error('❌ Falha ao deletar ações na API');
          setError('Falha ao deletar ações na API');
          return false;
        }
      } catch (err: any) {
        console.error('❌ Erro ao deletar ações na API:', err);
        setError(err.message || 'Erro ao deletar ações na API');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [actions]
  );

  // Hidratar ações existentes
  const hydrateActionsFromRule = useCallback(
    async (ruleUuid: string) => {
      console.log(`🔄 Hidratando ações da regra: ${ruleUuid}`);
      setLoading(true);
      setError(null);

      try {
        // Carregar definições de ações se necessário
        if (actionDefinitions.length === 0) {
          await loadActionDefinitions();
        }

        // Carregar ações da regra
        const apiActions = await actionsApi.getActionsByRule(ruleUuid);
        console.log('📥 Ações carregadas da API:', apiActions);

        // Converter para formato do frontend (não precisa de actionDefinitions pois vem junto)
        const formActions = convertFromApiActions(apiActions, []);
        setActions(formActions);

        console.log('✅ Ações hidratadas:', formActions);
      } catch (err: any) {
        console.error('❌ Erro ao hidratar ações:', err);
        setError(err.message || 'Erro ao hidratar ações');
      } finally {
        setLoading(false);
      }
    },
    [actionDefinitions, loadActionDefinitions]
  );

  // Limpar ações
  const clearActions = useCallback(() => {
    console.log('🧹 Limpando ações');
    setActions([]);
    setError(null);
  }, []);

  return {
    // Estado
    actions,
    actionDefinitions,
    teams,
    loading,
    error,
    searchFilters,

    // Ações
    addAction,
    removeAction,
    updateAction,
    updateActionDynamicField,

    // Busca e filtros
    setSearchTerm,
    getFilteredActions,

    // Validação
    validateActions: validateActionsFn,
    validateActionDynamicFields: validateActionDynamicFieldsFn,

    // Utilitários
    generateActionText: generateActionTextFn,
    generateDynamicFields: generateDynamicFieldsFn,

    // API
    loadActionDefinitions,
    loadTeams,
    createActionsInApi: createActionsInApiFn,
    updateActionsInApi: updateActionsInApiFn,
    deleteActionsInApi: deleteActionsInApiFn,

    // Hidratação
    hydrateActions: hydrateActionsFromRule,
    clearActions,
  };
};
